import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import backofficeService from '../../../services/backofficeService';
import CustomSelect from '../../../components/CustomSelect/CustomSelect';
import './ChallengeForm.css';

const emptyGame = {
  home_team: '',
  away_team: '',
  sport: 'tenis',
  pick: '',
  pick_description: '',
  game_odd: '',
  date: new Date().toISOString().split('T')[0],
  time: '12:00',
};

const ChallengeForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    added_amount: '',
    total_odd: '',
    result: '',
    games: [{ ...emptyGame }],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEditing) {
      loadChallenge();
    }
  }, [id]);

  const loadChallenge = async () => {
    setIsLoading(true);
    try {
      const challenge = await backofficeService.getChallenge(id);
      setFormData({
        date: challenge.date || '',
        amount: challenge.amount || '',
        added_amount: challenge.added_amount || '',
        total_odd: challenge.total_odd || '',
        result: challenge.result || '',
        games: challenge.games?.length > 0 ? challenge.games : [{ ...emptyGame }],
      });
    } catch (error) {
      setError('Erro ao carregar desafio');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGameChange = (index, field, value) => {
    setFormData(prev => {
      const updatedGames = [...prev.games];
      updatedGames[index] = { ...updatedGames[index], [field]: value };
      return { ...prev, games: updatedGames };
    });
  };

  const addGame = () => {
    setFormData(prev => ({
      ...prev,
      games: [...prev.games, { ...emptyGame }],
    }));
  };

  const removeGame = (index) => {
    if (formData.games.length === 1) return;
    setFormData(prev => ({
      ...prev,
      games: prev.games.filter((_, i) => i !== index),
    }));
  };

  const calculateTotalOdd = () => {
    const total = formData.games.reduce((acc, game) => {
      const odd = parseFloat(game.game_odd) || 1;
      return acc * odd;
    }, 1);
    setFormData(prev => ({ ...prev, total_odd: total.toFixed(2) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);

    try {
      const payload = {
        ...formData,
        games: formData.games.map(g => ({
          ...g,
          game_odd: g.game_odd ? parseFloat(g.game_odd) : null,
        })),
      };

      if (isEditing) {
        await backofficeService.updateChallenge(id, payload);
      } else {
        await backofficeService.createChallenge(payload);
      }
      navigate('/backoffice/challenges');
    } catch (err) {
      setError(err.message || 'Erro ao guardar desafio');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="backoffice-page">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>A carregar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="backoffice-page challenge-form-page">
      <div className="page-header">
        <h1>{isEditing ? 'Editar Desafio' : 'Novo Desafio'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="form-card">
        {error && <div className="form-error">{error}</div>}

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="date">Data</label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="amount">Montante</label>
            <input
              type="text"
              id="amount"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              placeholder="Ex: 10€"
            />
          </div>

          <div className="form-group">
            <label htmlFor="added_amount">Montante Adicionado</label>
            <input
              type="text"
              id="added_amount"
              name="added_amount"
              value={formData.added_amount}
              onChange={handleChange}
              placeholder="Ex: 5€"
            />
          </div>

          <div className="form-group">
            <label htmlFor="total_odd">Odd Total</label>
            <div className="input-with-button">
              <input
                type="text"
                id="total_odd"
                name="total_odd"
                value={formData.total_odd}
                onChange={handleChange}
                placeholder="Ex: 2.50"
              />
              <button type="button" onClick={calculateTotalOdd} className="btn-calc">
                Calcular
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="result">Resultado</label>
            <CustomSelect
              id="result"
              name="result"
              value={formData.result}
              onChange={handleChange}
              options={[
                { value: '', label: 'Pendente' },
                { value: 'won', label: 'Win' },
                { value: 'lost', label: 'Loss' },
                { value: 'void', label: 'Void' },
                { value: 'push', label: 'Push' },
              ]}
            />
          </div>
        </div>

        <div className="games-section">
          <div className="section-header">
            <h3>Jogos ({formData.games.length})</h3>
            <button type="button" onClick={addGame} className="btn-secondary">
              + Adicionar Jogo
            </button>
          </div>

          {formData.games.map((game, index) => (
            <div key={index} className="game-card">
              <div className="game-header">
                <span className="game-number">Jogo {index + 1}</span>
                {formData.games.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeGame(index)}
                    className="btn-remove"
                  >
                    Remover
                  </button>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Equipa Casa</label>
                  <input
                    type="text"
                    value={game.home_team || ''}
                    onChange={(e) => handleGameChange(index, 'home_team', e.target.value)}
                    placeholder="Ex: Porto"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Equipa Fora</label>
                  <input
                    type="text"
                    value={game.away_team || ''}
                    onChange={(e) => handleGameChange(index, 'away_team', e.target.value)}
                    placeholder="Ex: Benfica (opcional)"
                  />
                </div>

                <div className="form-group">
                  <label>Desporto</label>
                  <CustomSelect
                    name="sport"
                    value={game.sport || 'tenis'}
                    onChange={(e) => handleGameChange(index, 'sport', e.target.value)}
                    options={[
                      { value: 'futebol', label: 'Futebol' },
                      { value: 'basket', label: 'Basquetebol' },
                      { value: 'tenis', label: 'Ténis' },
                      { value: 'mma', label: 'MMA' },
                      { value: 'esports', label: 'Esports' },
                      { value: 'cs2', label: 'Counter Strike 2' },
                      { value: 'boxe', label: 'Boxe' },
                      { value: 'futsal', label: 'Futsal' },
                      { value: 'outros', label: 'Outro' },
                    ]}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Pick</label>
                  <input
                    type="text"
                    value={game.pick || ''}
                    onChange={(e) => handleGameChange(index, 'pick', e.target.value)}
                    placeholder="Ex: Over 2.5"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Descrição Pick</label>
                  <input
                    type="text"
                    value={game.pick_description || ''}
                    onChange={(e) => handleGameChange(index, 'pick_description', e.target.value)}
                    placeholder="Ex: Vencedor do jogo"
                  />
                </div>

                <div className="form-group">
                  <label>Odd</label>
                  <input
                    type="number"
                    step="0.01"
                    value={game.game_odd || ''}
                    onChange={(e) => handleGameChange(index, 'game_odd', e.target.value)}
                    placeholder="Ex: 1.85"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Data do Jogo</label>
                  <input
                    type="date"
                    value={game.date || ''}
                    onChange={(e) => handleGameChange(index, 'date', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Hora</label>
                  <input
                    type="time"
                    value={game.time ? game.time.slice(0, 5) : ''}
                    onChange={(e) => handleGameChange(index, 'time', e.target.value)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="form-actions">
          <Link to="/backoffice/challenges" className="btn-secondary">
            Cancelar
          </Link>
          <button type="submit" className="btn-primary" disabled={isSaving}>
            {isSaving ? 'A guardar...' : (isEditing ? 'Guardar Alterações' : 'Criar Desafio')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChallengeForm;
